<template>
    <div class="container">
        <h1>Les phases de Cardano (la feuille de route)</h1>
        <p class="intro">
            Une roadmap (feuille de route) est un résumé de la planification des développement. Voyons ensemble celle de Cardano.
        </p>

        <p>
            Le développement de Cardano sera livré en 5 <b>ères</b> différentes, chacune d'entre elle étant centrée sur un ensemble de fonctionnalités de la blockchain. Si les différentes <b>ères</b> arriveront séquentiellement, ce n'est en revanche
            pas le cas de leur processus de développement: ils sont tous réalisés en parallèle. La recherche, le prototypage et le développement sont réalisés sur différentes branches du projet qui fonctionnent en parallèle, afin d'accélérer le
            processus global du développement de Cardano.
        </p>

        <h2>Byron: les fondations du projet</h2>
        <img v-lazy-load data-src="/img/doc/cardano-byron.webp" style="float: left; margin-right: 20px; padding-bottom: 0px;" />
        <p>
            La phase Byron du projet a démarré en 2017, 2 ans après le début du projet, avec la sortie de la première version de la blockchain Cardano, et de sa cryptomonnaie, ADA. Byron est la phase de monitoring, de vérification et d'ajustement de
            la blockchain et de son protocole de consensus, Ouroboros (un protocole de
            <a href="/doc-cardano/qu-est-ce-que-le-delegated-proof-of-stake-dpos">DPoS</a>).
        </p>

        <p>Byron aura aussi été la phase de développement des portefeuilles <b>Daedalus</b> (full-node) et <b>Yoroi</b> (light), permettant aux premiers utilisateurs (non-technique) d'intéragir avec la blockchain et de s'envoyer des ADAs.</p>

        <p>
            Mais outre le développement technique de Cardano, Byron aura été avant tout la phase de développement de la première communauté Cardano, qui a réussi à passer d'un petit groupes d'enthousiastes à une communauté mondiale. La commaunauté
            compte désormais près de <b>500 000</b> membres, avec une cryptomonnaie parmi les plus adoptée du monde (listée dans le <b>top 10</b> des cryptomonnaies sur <a href="https://coinmarketcap.com"> CoinMarketCap</a>, et acceptée par tous les
            plus gros exchanges).
        </p>

        <h2>Shelley: décentralisation technique et géographique (nous sommes ici)</h2>
        <img v-lazy-load data-src="/img/doc/cardano-shelley.webp" style="float: left; margin-right: 20px; padding-bottom: 0px;" />
        <p>
            La phase Shelley a commencé fin juillet 2020, avec la release de l'ère Shelley sur la blockchain <i>mainnet</i> (la blockchain principale, par opposition à la blockchain <i>testnet</i> qui sert à tester les développements). La phase
            Shelley est l'ère de la décentralisation de la blockchain.
        </p>

        <p>
            Sous l'ère Byron, les blocs de la blockchain <i>mainnet</i> étaient intégralement créés par les noeuds d'IOHK. L'ère Shelley vise à distribuer, progressivement, la création des blocs des noeuds d'IOHK aux
            <a href="/doc-cardano/qu-est-ce-qu-une-stake-pool">Stake Pools</a>.
        </p>

        <p>
            Durant cette phase, le réseau est monitoré de très près afin de surveiller la distribution des blocs, la rentabilité des Stake Pools, la sécurité des transactions, la non-centralisation effective du réseau. Les paramètres de la
            blockchains (comme par exemple, les récompenses, le nombre désiré de Stake Pools, la saturation des Stake Pools, etc) sont progressivement ajusté au fur et à mesure que le réseau se développe, afin d'augmenter progressivement sa
            décentralisation tout en conservant les caractéristiques avantageuses d'ADA (frais de transaction bas, vitesse de transaction, etc).
        </p>

        <p>
            Avec la décentralisation de la blockchain nait le <a href="/doc-cardano/qu-est-ce-que-le-staking">staking</a>, les actionnaires (détenteurs d'ADA) sont désormais capable de participer au fonctionnement de la blockchain, en délégant
            (depuis leur portefeuille) leurs droit de vote à une Stake Pools, contre des intérêts (4% à 8% par an, actuellement).
        </p>

        <p>
            L'enjeu de Shelley est donc critique: le succès de cette phase fera d'ADA la première cryptomonnaie du top10 effectivement décentralisée s'étant affranchie des contraintes du Proof of Work. Voir notre article sur le
            <a href="/doc-cardano/qu-est-ce-que-le-delegated-proof-of-stake-dpos">DPoS</a> pour en savoir plus.
        </p>

        <h2>Goguen: les smarts contracts</h2>
        <img v-lazy-load data-src="/img/doc/cardano-goguen.webp" style="float: left; margin-right: 20px; padding-bottom: 0px;" />

        <p>3e phase du développement de la blockchain Cardano, Goguen implémentera les <b>smart contracts</b> (contrats intelligents) dans la blockchain Cardano. L'ère Goguen est développée en parallèle de Shelley.</p>

        <p>
            Les contrats intelligents permettront d'assurer, comme leur nom l'indique, l'exécution et la réalisation de contrats, mais pas par un tiers: par la blockchain directement. Ils sont la base des <i>dApps</i>, applications décentralisées
            (applications fonctionnant sur la blockchain), comme par exemple la <b>finance décentralisée</b> (exchanges décentralisée, assurances, etc), les <b>casino de cryptomonnaies</b>, les <b>jeux décentralisés</b>, etc. Actuellement, le marché
            actuel des plateforme smart contracts est de <b>52 milliards</b>, capitalisation combinée des différentes plateformes de smart contracts (plus de 15x la capitalisation de Cardano au moment ou j'écris ces lignes).
        </p>

        <p>
            Une fois Goguen dehors, au grand jour, il sera désormais possible d'exécuter des contrats intelligents sur une blockchain telle que Cardano,
            <a href="/doc-cardano/le-developpement-de-cardano">basée sur de la recherche revue par les pairs, et du développement prouvé</a>, ce qui est un avantage qu'on a pas besoin d'expliquer à qui souhaite exécuter des applications financières.
        </p>

        <p>
            L'un des objectifs de Goguen est de construire <mark>Plutus</mark>, un langage d'exécution qui viendra avec sa plateforme d'exécution, elle aussi basée sur <b>Haskell</b>. L'avantage de Haskell dans ce type d'application est clair: c'est
            un langage de programmation qui permet les démonstrations. On peut prouver que son code se comportera de telle ou telle façon, une capacité qui est plus que bienvenue, alors
            <a href="https://news.bitcoin.com/25-of-all-smart-contracts-contain-critical-bugs/">qu'on estime que 25% des smarts contracts actuels contiennent des bugs critiques</a> et que
            <a href="https://medium.com/chainsecurity/the-5-most-costly-ethereum-security-bugs-616c649b6c86">les bugs peuvent coûter des millions de dollars</a>.
        </p>

        <p>
            Au dessus de Plutus sera construit <mark>Marlowe</mark> et le <mark>Marlowe Playground</mark>, une version de Plutus simplifiée et spécialisée pour les applications financières à destination des non-programmeurs, l'autre étant une
            plateforme de visual-programming, donnant l'accès aux smarts contracts aux financiers qui n'ont jamais écrit une ligne de code.
        </p>

        <p>
            La combinaison de <b>Plutus</b> et de <b>Marlowe</b> vise à rendre les contrats intelligents accessibles au plus grand nombre et à démocratiser leur utilisation, afin de permettre aux particuliers et à l'industrie de s'en servir dans son
            quotidien (par exemple, pour se créer des assurances sur mesure, ou pour réaliser des opérations financières complexes).
        </p>

        <p>
            Goguen permettra également de créer des <b>tokens nativements supportés</b> par la blockchain, fonctionnalité qui sera supporté dès l'ajout du support multi-jeton dans les portefeuilles compatible Cardano. Les jetons crées pourront être
            <b>fongibles</b> (tous les jetons se valent. ex: des billets de 10€) ou <b>non-fongibles</b> (les jetons ne se valent pas. ex: des oeuvres d'art). Ces nouvelles cryptomonnaies seront donc supportées nativement par Cardano, et leur
            interaction avec des dApps facilitée.
        </p>

        <p>
            A l'issue de la phase Goguen, Cardano a donc pour objectif de pouvoir assurer toutes les fonctionnalitées d'Ethereum, mais le tout sur une base bien plus solide que celle-ci: une blockchain qui aura été pensée pour ça, depuis le départ,
            et qui pourra s'affranchir des
            <a href="https://cryptoast.fr/ethereum-problemes-defauts-limites/">défauts de celle-ci</a>, que <i>Charles Hoskinson</i> connait très bien.
        </p>

        <h2>Basho: la scalabilité</h2>

        <img v-lazy-load data-src="/img/doc/cardano-basho.webp" style="float: left; margin-right: 20px; padding-bottom: 0px;" />
        <p>
            Basho est l'ère de l'optimisation et de l'interopérabilité. Si les ères précédentes étaient concentrées sur la décentralisation et l'ajout de nouvelles fonctionnalités (sans, toutefois, perdre de vue les performances), Basho devrait
            supporter la croissance d'application décentralisées nécessitant un très grand volume de transactions.
        </p>

        <p>
            L'une des principale introduction de Basho sera les <mark>sidechains</mark>, de nouvelles blockchains interopérable avec la blockchain principale de Cardano, permettant d'étendre et d'adapter le réseaux à d'autres services et d'autres
            formes de collaboration (par exemple, l'interopérabilité avec d'autres blockchains ou d'autres réseaux). Les sidechains permettront également le développement de et l'expérimentation à échelle réelle de fonctionnalités sans risquer de
            compromettre pour autant la blockchain principale Cardano.
        </p>

        <h2>Voltaire: l'auto-gouvernance de la blockchain Cardano</h2>

        <img v-lazy-load data-src="/img/doc/cardano-voltaire.webp" style="float: left; margin-right: 20px; padding-bottom: 0px;" />
        <p>
            Pièce finale de la blockchain Cardano, Voltaire apportera la dernière forme de décentralisation (autre que géographique et technique): l'auto-gouvernance du réseau Cardano par les actionnaires de celui-ci, les détenteurs d'ADAs. Une
            blockchain n'est réellement décentralisée que si elle a la capacité de se maintenir et de s'améliorer, dans le temps, de façon décentralisée.
        </p>

        <p>
            Concrètement, les développements et la gestion de la trésorerie seront remise à la communauté. Les acteurs historiques de la blockchain seront alors simples membres de cette communauté, à même de proposer des développements en échange de
            financement par la trésorerie. La trésorerie, elle, sera constituée par l'allocation d'une petite fraction des frais de transactions.
        </p>

        <p>
            Chaque membre de la communauté (ouverte) pourra proposer des développements pour un budget, et l'allocation ou non des ressources sera décidée par la blockchain, à travers un système de vote. La distribution, les validations,
            l'intégration des nouveaux codes ou non seront également décidés par la communauté.
        </p>

        <p>
            Les acteurs historiques (comme IOHK) deviendront alors simple membres de la communauté, et seront à même de proposer de nouveaux développement, comme les autres.
        </p>

        <h2>Oû suivre l'avancée de Cardano</h2>
        <p>
            L'avancée du projet Cardano peut-être suivie:
        </p>
        <ul>
            <li>Via les <a href="https://roadmap.cardano.org/en/status-updates/">status update</a> officiels de la fondation Cardano</li>
            <li>Sur <a href="https://www.youtube.com/channel/UCiJiqEvUZxT6isIaXK7RXTg">la chaine Youtube de Charles Hoskinson</a> pour ceux qui sont plus friands de détails et de technique.</li>
        </ul>

        <div class="button-line">
            <a class="btn btn-outline-primary btn-left" href="/doc-cardano/le-developpement-de-cardano">
                <i class="fa fa-chevron-left"></i>
                <i>Précédent:</i>
                <b>Le développement de Cardano</b>
            </a>
            <a class="btn btn-outline-primary btn-right" href="/doc-cardano/les-defis-de-cardano">
                <i>Suivant:</i>
                <b>Les Défis de Cardano</b>
                <i class="fa fa-chevron-right"></i>
            </a>
        </div>
    </div>
</template>

<script>
import createHeader from "../../js/meta.js";

export default {
    layout: "DocLayout",
    head: createHeader({
        title: "Les phases de Cardano (la roadmap) - Stakepool.fr",
        description: "La feuille de route de Cardano, en détail. Qu'est-ce qui est prévu, et quand !",
    }),
};
</script>
